import { 
  startOfDay, 
  endOfDay, 
  setHours, 
  setMinutes, 
  addMinutes, 
  format,
  isSameDay,
  addDays
} from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { createDemoBooking, listBusyDemoSlots } from '../../api/demos/demos';

const DEMO_DURATION = 60; // minutes
const BUSINESS_HOURS = {
  start: 9, // 9 AM
  end: 17,  // 5 PM
};
const CENTRAL_TIMEZONE = 'America/Chicago';

// Helper function to normalize a date to hours and minutes for comparison
const normalizeTimeForComparison = (date) => {
  return setMinutes(setHours(startOfDay(date), date.getHours()), date.getMinutes());
};

// Helper function to convert a time from any timezone to Central time
const convertToCentralTime = (date, fromTimezone) => {
  // First convert to UTC
  const utcDate = zonedTimeToUtc(date, fromTimezone);
  // Then convert UTC to Central
  return utcToZonedTime(utcDate, CENTRAL_TIMEZONE);
};

// Generate time slots for a given date
export const getAvailableTimeSlots = async (date) => {
  try {
    const timeSlots = [];
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
    // First convert the date to start of day in user's timezone
    const userStartOfDay = startOfDay(date);
    
    // Convert to Central timezone
    const centralDate = utcToZonedTime(zonedTimeToUtc(userStartOfDay, userTimezone), CENTRAL_TIMEZONE);
    
    // If the central date is a day behind, add a day to get to the correct date
    const adjustedCentralDate = isSameDay(date, centralDate) ? centralDate : addDays(centralDate, 1);
    
    // Set up the start and end of business hours in Central time
    const startTime = setHours(setMinutes(adjustedCentralDate, 0), BUSINESS_HOURS.start);
    const endTime = setHours(setMinutes(adjustedCentralDate, 0), BUSINESS_HOURS.end);
    
    // Get the UTC range for the full day in Central time
    const dayStartUtc = zonedTimeToUtc(startOfDay(adjustedCentralDate), CENTRAL_TIMEZONE);
    const dayEndUtc = zonedTimeToUtc(endOfDay(adjustedCentralDate), CENTRAL_TIMEZONE);
    
    let busy;
    try {
      busy = await listBusyDemoSlots({
        from: dayStartUtc.toISOString(),
        to: dayEndUtc.toISOString(),
      });
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw new Error('Failed to fetch available time slots. Please try again.');
    }
    
    const existingBookings = busy
      .map((item) => {
        const bookingDate = new Date(item.dateTime);
        const bookingInCentral = utcToZonedTime(bookingDate, CENTRAL_TIMEZONE);
        return {
          ...item,
          dateTime: bookingInCentral,
        };
      })
      .filter(booking => {
        const isValid = (booking.status === 'scheduled' || booking.status === 'completed') &&
                       isSameDay(booking.dateTime, adjustedCentralDate);
        return isValid;
      });

    // Generate all possible time slots
    let currentSlot = startTime;
    while (currentSlot < endTime) {
      const slotEnd = addMinutes(currentSlot, DEMO_DURATION);
      
      // Convert the Central time slot to user's timezone for display
      const slotUtc = zonedTimeToUtc(currentSlot, CENTRAL_TIMEZONE);
      const slotInUserTz = utcToZonedTime(slotUtc, userTimezone);
      const currentSlotStr = format(slotInUserTz, 'h:mm a');
      
      // Check if this time slot conflicts with any existing booking
      const isSlotTaken = existingBookings.some(booking => {
        const normalizedBookingTime = normalizeTimeForComparison(booking.dateTime);
        const normalizedCurrentSlot = normalizeTimeForComparison(currentSlot);
        const normalizedSlotEnd = addMinutes(normalizedCurrentSlot, DEMO_DURATION);
        const normalizedBookingEnd = addMinutes(normalizedBookingTime, DEMO_DURATION);

        const hasOverlap = (
          (normalizedCurrentSlot >= normalizedBookingTime && normalizedCurrentSlot < normalizedBookingEnd) ||
          (normalizedSlotEnd > normalizedBookingTime && normalizedSlotEnd <= normalizedBookingEnd) ||
          (normalizedCurrentSlot <= normalizedBookingTime && normalizedSlotEnd >= normalizedBookingEnd)
        );
        
        return hasOverlap;
      });

      // Only add the slot if it's not taken
      if (!isSlotTaken) {
        // Store both the display time (user timezone) and the actual time (Central)
        timeSlots.push({
          display: currentSlotStr,
          time: format(currentSlot, 'HH:mm')
        });
      }

      currentSlot = addMinutes(currentSlot, DEMO_DURATION);
    }

    // Return only the display times for backward compatibility
    return timeSlots.map(slot => slot.display);
  } catch (error) {
    console.error('Error in getAvailableTimeSlots:', error);
    throw new Error('Failed to load available time slots. Please try again.');
  }
};

export const saveDemoBooking = async (bookingData) => {
  const userTimezone = bookingData.timezone;
  
  // Parse the selected time and convert it to Central timezone
  const [hours, minutes] = format(bookingData.dateTime, 'HH:mm').split(':').map(Number);
  const dateInCentral = setHours(setMinutes(bookingData.dateTime, minutes), hours);
  const centralDateTime = convertToCentralTime(dateInCentral, userTimezone);

  try {
    const dayStart = startOfDay(centralDateTime);
    const dayEnd = endOfDay(centralDateTime);
    const busy = await listBusyDemoSlots({
      from: zonedTimeToUtc(dayStart, CENTRAL_TIMEZONE).toISOString(),
      to: zonedTimeToUtc(dayEnd, CENTRAL_TIMEZONE).toISOString(),
    });
    
    const hasConflict = busy
      .filter((item) => {
        const bookingDate = new Date(item.dateTime);
        const bookingInCentral = utcToZonedTime(bookingDate, CENTRAL_TIMEZONE);
        return (
          (item.status === 'scheduled' || item.status === 'completed') &&
          isSameDay(bookingInCentral, centralDateTime)
        );
      })
      .some((item) => {
        const bookingDate = new Date(item.dateTime);
        const bookingInCentral = utcToZonedTime(bookingDate, CENTRAL_TIMEZONE);
        
        const normalizedExistingTime = normalizeTimeForComparison(bookingInCentral);
        const normalizedNewTime = normalizeTimeForComparison(centralDateTime);
        const normalizedExistingEnd = addMinutes(normalizedExistingTime, DEMO_DURATION);
        const normalizedNewEnd = addMinutes(normalizedNewTime, DEMO_DURATION);

        const hasOverlap = (
          (normalizedNewTime >= normalizedExistingTime && normalizedNewTime < normalizedExistingEnd) ||
          (normalizedNewEnd > normalizedExistingTime && normalizedNewEnd <= normalizedExistingEnd) ||
          (normalizedNewTime <= normalizedExistingTime && normalizedNewEnd >= normalizedExistingEnd)
        );

        return hasOverlap;
      });

    if (hasConflict) {
      throw new Error('This time slot is no longer available. Please select a different time.');
    }

    // Convert the Central time to UTC for storage
    const utcDate = zonedTimeToUtc(centralDateTime, CENTRAL_TIMEZONE);
    return createDemoBooking({
      ...bookingData,
      dateTime: utcDate.toISOString(),
      centralDateTime: format(centralDateTime, 'yyyy-MM-dd HH:mm:ss'),
      status: 'scheduled',
    });
  } catch (error) {
    console.error('Booking error:', error);
    throw error;
  }
}; 